import React, { useRef } from 'react'
import { useElementHeight } from 'hooks/useElementSize'
import LargeContentToggle from 'components/Common/LargeContentToggle'

interface Props {
  children: React.ReactNode
  showMoreText?: string
  showLessText?: string
  maxHeight: number
  /**
   * Minimum pixels content must exceed maxHeight before showing toggle.
   * Prevents flickering when heights are nearly equal due to measurement variations.
   * @default 24
   */
  threshold?: number
}

/**
 * A wrapper around LargeContentToggle that adds threshold logic to prevent
 * the "View more" toggle from appearing when content barely exceeds maxHeight.
 *
 * This component is specific to Flash Hotel inclusions to avoid modifying
 * the shared LargeContentToggle component.
 */
function FlashHotelInclusionsToggle({
  children,
  maxHeight,
  threshold = 24,
  showLessText,
  showMoreText,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null)
  const contentHeight = useElementHeight(contentRef)

  // If measured and content fits within threshold, show all content (no clipping)
  const fitsWithinThreshold = contentHeight !== null && contentHeight <= maxHeight + threshold

  // Use actual content height when it fits (so LargeContentToggle won't clip or show toggle)
  // During initial measurement (null) or when content exceeds threshold, use maxHeight for clipping
  // This prevents the flash where content shows fully then gets clipped
  const effectiveMaxHeight = fitsWithinThreshold ? contentHeight : maxHeight

  return (
    <LargeContentToggle
      maxHeight={effectiveMaxHeight}
      minimumContentHeight={contentHeight ?? 0}
      showMoreText={showMoreText}
      showLessText={showLessText}
    >
      <div ref={contentRef}>{children}</div>
    </LargeContentToggle>
  )
}

export default FlashHotelInclusionsToggle
